import { useEffect, useId, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanBarcode, Camera } from "lucide-react";

export function ScanInput({ label, value, onChange, placeholder, testid, autoFocus }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-1.5">
      {label && <label className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</label>}
      <div className="flex gap-2">
        <Input
          data-testid={testid}
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Type or scan serial number"}
          className="bg-slate-50 border-border font-mono"
        />
        <Button type="button" variant="outline" data-testid={`${testid}-scan-btn`}
          onClick={() => setOpen(true)} className="shrink-0 border-border">
          <ScanBarcode className="h-4 w-4 mr-1.5" /> Scan
        </Button>
      </div>
      <CameraScanner open={open} onOpenChange={setOpen}
        onResult={(text) => { onChange(text); setOpen(false); }} testid={`${testid}-camera`} />
    </div>
  );
}

export function CameraScanner({ open, onOpenChange, onResult, testid }) {
  const rawId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const elId = `qr-reader-${rawId}`;
  const [camError, setCamError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCamError(false);
    let cancelled = false;
    let scanner = null;
    let attempts = 0;
    const startScanner = () => {
      if (cancelled) return;
      const el = document.getElementById(elId);
      if (!el) {
        if (attempts++ < 10) setTimeout(startScanner, 150);
        else setCamError(true);
        return;
      }
      try {
        scanner = new Html5Qrcode(elId);
        scanner
          .start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 240, height: 240 } },
            (text) => { if (!cancelled) onResult(text); }, () => {})
          .catch(() => { if (!cancelled) setCamError(true); });
      } catch {
        if (!cancelled) setCamError(true);
      }
    };
    const timer = setTimeout(startScanner, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (scanner) {
        try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch { /* not running */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Camera className="h-5 w-5 text-emerald-400" /> Scan Battery Barcode / QR
          </DialogTitle>
        </DialogHeader>
        <div id={elId} className="w-full rounded-lg overflow-hidden bg-black min-h-[280px]" />
        {camError && (
          <p className="text-sm text-amber-400" data-testid={`${testid}-error`}>
            Camera unavailable. Allow camera permission or type the serial number manually.
          </p>
        )}
        <p className="text-xs text-slate-500">Point the camera at the battery's barcode or QR code.</p>
      </DialogContent>
    </Dialog>
  );
}
